import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Scale, X, ArrowLeftRight } from "lucide-react";
import { formatRON, mediaUrl } from "@/lib/api";
import { metrics } from "@/components/ListingMatrix";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=200";
const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

export default function CompareMachines({ items, onRemove, onClear }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!items.length) return null;

  const rows = [
    { key: "power", label: `${t("ux.matrix.power")} (kW)`, get: (m) => dash(m.power) },
    { key: "capacity", label: `${t("ux.matrix.capacity")} (m³)`, get: (m) => dash(m.capacity) },
    { key: "rpm", label: t("ux.matrix.rpm"), get: (m) => dash(m.rpm) },
    { key: "weight", label: `${t("ux.matrix.weight")} (kg)`, get: (m) => dash(m.weight) },
    { key: "year", label: t("ux.matrix.year"), get: (m) => dash(m.year) },
  ];

  return (
    <>
      {/* Floating comparison action bar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92%,720px)] bg-slate-900 text-white border border-amber-500/60 shadow-2xl px-3 py-2.5 flex items-center gap-3" data-testid="compare-bar">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide shrink-0"><Scale className="w-4 h-4 text-amber-400" /> {items.length}/3 {t("ux.compare.selected")}</span>
        <div className="flex-1 flex items-center gap-1.5 overflow-x-auto min-w-0">
          {items.map((it) => (
            <span key={it.id} className="inline-flex items-center gap-1 bg-white/10 px-2 py-1 text-[11px] whitespace-nowrap max-w-[180px]">
              <span className="truncate">{it.name}</span>
              <button onClick={() => onRemove(it)} className="text-white/60 hover:text-white shrink-0" data-testid={`compare-remove-${it.id}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <button onClick={onClear} className="text-xs text-white/60 hover:text-white shrink-0" data-testid="compare-clear">{t("ux.compare.clear")}</button>
        <button onClick={() => setOpen(true)} disabled={items.length < 2}
          className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 px-3.5 py-2 text-xs font-black uppercase tracking-wide transition-colors shrink-0" data-testid="compare-open">
          <ArrowLeftRight className="w-4 h-4" /> {t("ux.compare.action")}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none p-0 gap-0" data-testid="compare-modal">
          <DialogHeader className="px-5 py-3 border-b border-slate-200">
            <DialogTitle className="flex items-center gap-2 text-base font-black"><Scale className="w-5 h-5 text-amber-500" /> {t("ux.compare.title")}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-[10px] uppercase tracking-wide font-bold text-slate-500 px-3 py-2 w-32">—</th>
                  {items.map((it) => (
                    <th key={it.id} className="px-3 py-2 text-left align-top" data-testid={`compare-col-${it.id}`}>
                      <div className="w-16 h-16 bg-slate-100 border border-slate-200 overflow-hidden mb-1.5">
                        <img src={mediaUrl(it.image) || PLACEHOLDER} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-full h-full object-cover" />
                      </div>
                      {it.brand && <div className="text-[10px] uppercase tracking-wide text-orange-600 font-bold">{it.brand}</div>}
                      <div className="font-bold text-slate-900 leading-tight text-xs max-w-[160px]">{it.name}</div>
                      <div className="text-[11px] text-slate-500">{it.location || it.county || "—"}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {rows.map((r) => (
                  <tr key={r.key} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-[11px] uppercase tracking-wide font-bold text-slate-500 font-sans">{r.label}</td>
                    {items.map((it) => <td key={it.id} className="px-3 py-2 text-slate-800 font-semibold">{r.get(metrics(it))}</td>)}
                  </tr>
                ))}
                <tr className="bg-orange-50/60">
                  <td className="px-3 py-2 text-[11px] uppercase tracking-wide font-bold text-slate-600 font-sans">{t("ux.matrix.price")}</td>
                  {items.map((it) => (
                    <td key={it.id} className="px-3 py-2 font-sans font-black text-slate-900">{it.price_on_request || !it.price ? t("pd.priceOnRequest") : formatRON(it.price)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
